var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import ParserModule from "./ParserModule";
import ConstantConverter from "./ConstantConverter";
import Texture2D from "grimoirejs-fundamental/ref/Resource/Texture2D";
import Geometry from "grimoirejs-fundamental/ref/Geometry/Geometry";
import MorphGeometry from "grimoirejs-fundamental/ref/Geometry/MorphGeometry";
import GLTFConstantConverter from "./ConstantConverter";
import Vector3 from "grimoirejs-math/ref/Vector3";
import GLTFMaterialInstanciatorRegistry from "./MaterialInstanciator/GLTFMaterialInstanciatorRegistry";
export default class DefaultParserModule extends ParserModule {
    fetchGLTF(url) {
        return this.__fetchBuffer(url);
    }
    loadAsGLTF(buffer) {
        const rawStr = this.__bufferToString(buffer);
        return JSON.parse(rawStr);
    }
    loadTextureResources(tf) {
        return __awaiter(this, void 0, void 0, function* () {
            const textures = {};
            const promises = [];
            if (tf.images) {
                for (let key in tf.textures) {
                    const texture = tf.textures[key];
                    const promise = this.parser.callParserModule(t => t.fetchTextureResource, tf.images[texture.source])
                        .then(img => {
                        const texture = this.parser.callParserModule(t => t.convertTotexture, { tf: tf, image: img, texIndex: key });
                        textures[key] = texture;
                    });
                    promises.push(promise);
                }
            }
            yield Promise.all(promises);
            return textures;
        });
    }
    /**
     * Start loading texture resource.
     * @return {Promise<Texture2D>} [description]
     */
    fetchTextureResource(tf) {
        return this.__fetchImage(this.__asAbsoluteURL(this.baseDirectory, tf.uri));
    }
    /**
     * Load image as texture
     * @return {Promise<Texture2D>} [description]
     */
    convertTotexture(arg) {
        const tex = new Texture2D(this.__gl);
        tex.update(arg.image, { flipY: false });
        const texInfo = arg.tf.textures[arg.texIndex];
        let samplerInfo = {};
        if (texInfo && texInfo.sampler !== void 0) {
            samplerInfo = arg.tf.samplers[texInfo.sampler];
        }
        tex.magFilter = samplerInfo.magFilter || WebGLRenderingContext.LINEAR;
        tex.minFilter = samplerInfo.minFilter || WebGLRenderingContext.NEAREST_MIPMAP_LINEAR;
        tex.wrapS = samplerInfo.wrapS || WebGLRenderingContext.REPEAT;
        tex.wrapT = samplerInfo.wrapT || WebGLRenderingContext.REPEAT;
        return tex;
    }
    /**
     * Start loading buffer resource.
     * @return {Promise<ArrayBuffer>} [description]
     */
    loadBufferResource(tf) {
        return this.__fetchBuffer(this.__asAbsoluteURL(this.baseDirectory, tf.uri));
    }
    loadBufferResources(tf) {
        return __awaiter(this, void 0, void 0, function* () {
            const buffers = {};
            const promises = [];
            for (let key in tf.buffers) {
                promises.push(this.parser.callParserModule(t => t.loadBufferResource, tf.buffers[key]).then(buffer => {
                    buffers[key] = buffer;
                }));
            }
            yield Promise.all(promises);
            return buffers;
        });
    }
    loadBufferViews(args) {
        const bufferViews = {};
        for (let key in args.tf.bufferViews) {
            const bufferViewInfo = args.tf.bufferViews[key];
            if (bufferViewInfo.byteOffset === void 0) {
                bufferViewInfo.byteOffset = 0;
            }
            bufferViews[key] = new Uint8Array(args.buffers[bufferViewInfo.buffer], bufferViewInfo.byteOffset, bufferViewInfo.byteLength);
        }
        return bufferViews;
    }
    loadPrimitivesOfMesh(args) {
        const result = {};
        for (let key in args.tf.meshes) {
            const meshInfo = args.tf.meshes[key];
            const primitives = [];
            result[key] = primitives;
            for (let pKey in meshInfo.primitives) {
                primitives.push(this.parser.callParserModule(t => t.loadPrimitive, { tf: args.tf, bufferViews: args.bufferViews, primitive: meshInfo.primitives[pKey] }));
            }
        }
        return result;
    }
    loadPrimitive(args) {
        const geo = args.primitive.targets === void 0 ? new Geometry(this.__gl) : new MorphGeometry(this.__gl);
        this.parser.callParserModule(t => t.appendIndices, { tf: args.tf, bufferViews: args.bufferViews, primitive: args.primitive, geometry: geo });
        this.parser.callParserModule(t => t.addVertexAttributes, { tf: args.tf, bufferViews: args.bufferViews, primitive: args.primitive, geometry: geo });
        return geo;
    }
    appendIndices(args) {
        if (args.primitive.indices !== void 0) {
            const topology = args.primitive.mode || WebGLRenderingContext.TRIANGLES;
            const indexAccessor = args.tf.accessors[args.primitive.indices];
            args.geometry.addIndex("default", args.bufferViews[indexAccessor.bufferView], topology, indexAccessor.byteOffset, indexAccessor.count, indexAccessor.componentType);
            return true;
        }
    }
    addVertexAttributes(args) {
        for (let attrib in args.primitive.attributes) {
            const primitive = args.primitive;
            const accessor = args.tf.accessors[primitive.attributes[attrib]];
            const bufferViewInfo = args.tf.bufferViews[accessor.bufferView];
            const bufAccessor = {};
            const elementSize = GLTFConstantConverter.asVectorSize(accessor.type);
            // Check morph used. If morph was used for spcified attribute, source of the buffer should be keeped for using later.
            let useMorphing = false;
            if (primitive.targets && primitive.targets.length >= 1) {
                for (let target of primitive.targets) {
                    if (attrib in target) {
                        useMorphing = true;
                        break;
                    }
                }
            }
            bufAccessor[attrib] = {
                size: elementSize,
                type: accessor.componentType,
                stride: bufferViewInfo.byteStride,
                offset: 0,
                keepOnBuffer: useMorphing
            };
            const bufferView = args.bufferViews[accessor.bufferView];
            const ctor = ConstantConverter.asTypedArrayConstructor(accessor.componentType);
            const convertedBuffer = this.__convertBufferView(ctor, bufferView, bufferViewInfo, accessor);
            args.geometry.addAttributes(convertedBuffer, bufAccessor);
            if (attrib === "POSITION") {
                let stride = bufferViewInfo.byteStride / 4;
                if (isNaN(stride)) {
                    stride = 3; //ConstantConverter.asVectorSize(accessor.type) / ConstantConverter.asByteSize(accessor.componentType);
                }
                for (let j = 0; j < accessor.count; j++) {
                    let first = j * stride;
                    args.geometry.aabb.expand(new Vector3(convertedBuffer[first], convertedBuffer[first + 1], convertedBuffer[first + 2]));
                }
            }
            if (args.primitive.targets !== void 0 && args.primitive.targets[0][attrib] !== void 0) {
                // This attribute has morph
                const geometry = args.geometry;
                let parameters = [];
                const targets = args.primitive.targets;
                for (let i = 0; i < targets.length; i++) {
                    const accessor = args.tf.accessors[targets[i][attrib]];
                    const bufferViewInfo = args.tf.bufferViews[accessor.bufferView];
                    const buffer = args.bufferViews[accessor.bufferView];
                    const morphBuffer = this.__convertBufferView(Float32Array, buffer, bufferViewInfo, accessor);
                    parameters.push({
                        buffer: morphBuffer,
                        accessor: {
                            size: GLTFConstantConverter.asVectorSize(accessor.type),
                            stride: bufferViewInfo.byteStride,
                            offset: 0
                        }
                    });
                }
                geometry.addMorphAttribute(attrib, parameters);
            }
        }
        this.parser.callParserModule(t => t.complementVertexAttributes, args);
        return true;
    }
    complementVertexAttributes(args) {
        return true;
    }
    loadMaterials(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = {};
            for (let key in args.tf.materials) {
                result[key] = yield this.parser.callParserModule(t => t.loadMaterial, { material: args.tf.materials[key], textures: args.textures });
            }
            return result;
        });
    }
    loadMaterial(args) {
        return __awaiter(this, void 0, void 0, function* () {
            return GLTFMaterialInstanciatorRegistry.get(this.__gl).getInstanciator(args.material, args.textures, args.tf, this.parser);
        });
    }
    loadAnimations(args) {
        const result = {};
        for (let key in args.tf.animations) {
            const animation = args.tf.animations[key];
            result[key] = this.parser.callParserModule(m => m.loadAnimation, { tf: args.tf, bufferViews: args.bufferViews, animation: animation });
        }
        return result;
    }
    loadAnimation(args) {
        const defaultClip = [];
        for (let i = 0; i < args.animation.channels.length; i++) {
            let clip = {};
            const channel = args.animation.channels[i];
            const query = ".gltf-node-" + channel.target.node;
            const target = this._pathNameToGrimoire(channel.target.path);
            const sampler = args.animation.samplers[channel.sampler];
            clip.query = query;
            clip.component = target.component;
            clip.attribute = target.attributeName;
            const inputAccessor = args.tf.accessors[sampler.input];
            const outputAccessor = args.tf.accessors[sampler.output];
            const inputBufferInfo = args.tf.bufferViews[inputAccessor.bufferView];
            const inputBuffer = args.bufferViews[inputAccessor.bufferView];
            const outputBuffer = args.bufferViews[outputAccessor.bufferView];
            const outputBufferInfo = args.tf.bufferViews[outputAccessor.bufferView];
            const inputBufferF32 = this.__convertBufferView(Float32Array, inputBuffer, inputBufferInfo, inputAccessor); //new Float32Array(inputBuffer.buffer, inputBuffer.byteOffset + inputAccessor.byteOffset, inputAccessor.count);
            const outputBufferF32 = this.__convertBufferView(Float32Array, outputBuffer, outputBufferInfo, outputAccessor); //new Float32Array(outputBuffer.buffer, outputBuffer.byteOffset + outputAccessor.byteOffset, outputAccessor.count * elemCount);
            const elemCount = outputBufferF32.length / inputBufferF32.length;
            const times = new Array(inputAccessor.count);
            for (let i = 0; i < inputAccessor.count; i++) {
                times[i] = inputBufferF32[i] * 1000; // SHould consider buffer stride
            }
            clip.timeline = times;
            clip.defaultEffect = "LINEAR"; // TODO bug of animation plugin?
            let values = [];
            for (let i = 0; i < outputAccessor.count; i++) {
                values[i] = [];
                for (let j = 0; j < elemCount; j++) {
                    values[i][j] = outputBufferF32[i * elemCount + j]; // SHould consider buffer stride
                }
            }
            clip.values = values;
            defaultClip.push(clip);
        }
        return {
            default: defaultClip
        };
    }
    _pathNameToGrimoire(name) {
        switch (name) {
            case "translation":
                return { component: "Transform", attributeName: "position" };
            case "rotation":
                return { component: "Transform", attributeName: "rotation" };
            case "scale":
                return { component: "Transform", attributeName: "scale" };
            case "weights":
                return { component: "GLTFVertexMorpher", attributeName: "weights" };
            default:
                throw new Error("Unsupported path type on grimoire");
        }
    }
}



// WEBPACK FOOTER //
// ./src/Parser/DefaultParserModule.ts